import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Skeleton,
  Alert,
  Chip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axiosInstance from 'src/utils/axios';
import Iconify from 'src/components/iconify';

export default function FormCompOffDateSelector({
  name,
  label,
  apiEndpoint,
  required = false,
  disabled = false,
  helperText = '',
  getOptionLabel,
  getOptionValue,
  ...other
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available comp off dates
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get(apiEndpoint);
        const data = response.data?.data || response.data || [];
        setOptions(data);
      } catch (err) {
        console.error('Failed to fetch comp off dates:', err);
        setError(err.response?.data?.message || 'Failed to load available dates');
      } finally {
        setLoading(false);
      }
    };

    if (apiEndpoint) {
      fetchOptions();
    }
  }, [apiEndpoint]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDayType = (option) => {
    if (option.type === 'Sunday') return 'Sunday';
    return option.holidayName || 'Holiday';
  };

  const getDayTypeColor = (option) => {
    if (option.type === 'Sunday') return 'primary';
    return 'success';
  };

  const getDayTypeIcon = (option) => {
    if (option.type === 'Sunday') return 'mdi:calendar-weekend';
    return 'mdi:party-popper';
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          error={!!errors[name]}
          required={required}
          disabled={disabled}
          fullWidth
          {...other}
        >
          {label && (
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              {label}
            </FormLabel>
          )}

          {/* Loading State */}
          {loading && (
            <Grid container spacing={2}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Skeleton variant="rounded" height={100} />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Error State */}
          {error && !loading && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && options.length === 0 && (
            <Alert severity="info" icon={<Iconify icon="mdi:information-outline" />}>
              No comp off dates available. You need to work on a weekend or holiday first.
            </Alert>
          )}

          {/* Date Cards */}
          {!loading && !error && options.length > 0 && (
            <>
              <Grid container spacing={2}>
                {options.map((option, index) => {
                  // Support both single value (object) and array of values
                  const currentValue = field.value;
                  let isSelected = false;

                  if (Array.isArray(currentValue)) {
                    // Multiple selection mode
                    isSelected = currentValue.some((item) => item?.date === option.date);
                  } else if (currentValue?.date) {
                    // Single selection mode (backward compatible)
                    isSelected = currentValue.date === option.date;
                  }

                  const dayType = getDayType(option);
                  const dayTypeColor = getDayTypeColor(option);
                  const dayTypeIcon = getDayTypeIcon(option);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={option.date || index}>
                      <Card
                        sx={{
                          border: 2,
                          borderColor: isSelected ? 'primary.main' : 'transparent',
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: isSelected
                              ? 'primary.main'
                              : alpha(theme.palette.primary.main, 0.3),
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[4],
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => {
                            const optionValue = getOptionValue ? getOptionValue(option) : option;
                            const fieldValue = field.value;

                            // Check if we're in array mode or should switch to it
                            if (Array.isArray(fieldValue)) {
                              // Multiple selection mode - toggle the date
                              const isAlreadySelected = fieldValue.some(
                                (item) => item?.date === option.date
                              );

                              if (isAlreadySelected) {
                                // Remove from selection
                                const newValue = fieldValue.filter(
                                  (item) => item?.date !== option.date
                                );
                                field.onChange(newValue);
                              } else {
                                // Add to selection
                                field.onChange([...fieldValue, optionValue]);
                              }
                            } else if (fieldValue?.date === option.date) {
                              // Deselect - set to empty array
                              field.onChange([]);
                            } else if (fieldValue?.date) {
                              // Already has one selected, add this one
                              field.onChange([fieldValue, optionValue]);
                            } else {
                              // First selection - start array
                              field.onChange([optionValue]);
                            }
                          }}
                          disabled={disabled}
                          sx={{ p: 2 }}
                        >
                          <Box>
                            {/* Date Display */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1.5,
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {formatDate(option.date)}
                              </Typography>
                              {isSelected && (
                                <Iconify
                                  icon="mdi:check-circle"
                                  width={24}
                                  sx={{ color: 'primary.main' }}
                                />
                              )}
                            </Box>

                            {/* Day Type Badge */}
                            <Chip
                              icon={<Iconify icon={dayTypeIcon} width={16} />}
                              label={dayType}
                              color={dayTypeColor}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  ml: 0.5,
                                },
                              }}
                            />
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Selection Counter */}
              {Array.isArray(field.value) && field.value.length > 0 && (
                <Alert severity="success" icon={<Iconify icon="mdi:check-circle" />} sx={{ mt: 2 }}>
                  {field.value.length} comp off date{field.value.length > 1 ? 's' : ''} selected
                </Alert>
              )}
            </>
          )}

          {/* Helper Text / Error Message */}
          {errors[name] && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errors[name]?.message}
            </FormHelperText>
          )}
          {helperText && !errors[name] && (
            <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

FormCompOffDateSelector.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  apiEndpoint: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
};
